import styles from "./Column.module.scss";
import { TextCard } from "../../cardComponents/TextCard";
import { WrappingCard } from "../../cardComponents/WrappingCard";
import { ImageCard } from "../../cardComponents/ImageCard";
import { TextImageCard } from "../../cardComponents/TextImageCard";
import { TextAudioCard } from "../../cardComponents/TextAudioCard";
import { AudioCard } from "../../cardComponents/AudioCard";
import { useRef, useLayoutEffect, useImperativeHandle, forwardRef, useCallback } from "react";
import { motion } from "framer-motion";
import { wait } from "../../utils/wait";
import { CARD_TYPES, TYPE_OF_COLUMN } from "../../utils/constants";
import clsx from "clsx";

function typeSelector(element) {
  if (!element.media) {
    return CARD_TYPES.TEXT;
  }
  if (element.media.type === "image") {
    return element.value != null && element.value !== ""
      ? CARD_TYPES.TEXT_IMAGE
      : CARD_TYPES.IMAGE;
  }
  if (element.media.type === "audio") {
    return element.value != null && element.value !== ""
      ? CARD_TYPES.TEXT_AUDIO
      : CARD_TYPES.AUDIO;
  }
  return CARD_TYPES.TEXT;
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1,
    },
  },
  exit: {
    opacity: 0,
    transition: {
      staggerChildren: 0.05,
      staggerDirection: -1,
    },
  },
};

export const Column = forwardRef(function Column(
  {
    elements,
    onSelect,
    selectedElement,
    disabled,
    onSizeChanged,
    position,
    wrong,
    limLives,
    scrollContainerRef,
  },
  ref
) {
  const columnRef = useRef(null);
  const circleRefs = useRef({});
  const resizeObserverRef = useRef(null);
  const updateTimeoutRef = useRef(null);

  useImperativeHandle(ref, () => ({
    animateExit: async () => {
      await wait(400);
    },
    animateEntry: async () => {
      await wait(100);
    },
  }));

  const updateCirclePositions = useCallback(() => {
    if (updateTimeoutRef.current) {
      cancelAnimationFrame(updateTimeoutRef.current);
    }

    updateTimeoutRef.current = requestAnimationFrame(() => {
      const newCirclePositions = Object.entries(circleRefs.current)
        .filter(([, ref]) => ref != null)
        .map(([id, ref]) => {
          const circleRect = ref.getBoundingClientRect();
          
          return {
            id: parseInt(id),
            x: circleRect.left + circleRect.width / 2,
            y: circleRect.top + circleRect.height / 2,
          };
        });
      onSizeChanged(newCirclePositions);
    });
  }, [onSizeChanged]);

  useLayoutEffect(() => {
    const columnNode = columnRef.current;
    if (!columnNode) return;

    resizeObserverRef.current = new ResizeObserver(updateCirclePositions);

    resizeObserverRef.current.observe(columnNode);

    updateCirclePositions();

    const initialTimeoutId = setTimeout(() => {
      updateCirclePositions();
    }, 100);

    let scrollRafId = null;
    const handleScroll = () => {
      if (scrollRafId !== null) {
        cancelAnimationFrame(scrollRafId);
      }
      scrollRafId = requestAnimationFrame(() => {
        updateCirclePositions();
        scrollRafId = null;
      });
    };

    let scrollContainer = scrollContainerRef?.current;
    let rafId = null;

    const attachListener = () => {
      scrollContainer = scrollContainerRef?.current;
      if (scrollContainer) {
        scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
      } else {
        rafId = requestAnimationFrame(attachListener);
      }
    };

    attachListener();

    const handleImageLoad = () => {
      updateCirclePositions();
    };

    const images = columnNode.querySelectorAll('img');
    images.forEach((img) => {
      if (img.complete) {
        setTimeout(() => updateCirclePositions(), 50);
      } else {
        img.addEventListener('load', handleImageLoad);
      }
    });

    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
      if (updateTimeoutRef.current) {
        cancelAnimationFrame(updateTimeoutRef.current);
      }
      if (initialTimeoutId) {
        clearTimeout(initialTimeoutId);
      }
      if (scrollRafId !== null) {
        cancelAnimationFrame(scrollRafId);
      }
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', handleScroll);
      }
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      const imgs = columnNode.querySelectorAll('img');
      imgs.forEach((img) => {
        img.removeEventListener('load', handleImageLoad);
      });
    };
  }, [updateCirclePositions, scrollContainerRef]);

  return (
    <motion.div
      className={styles.column}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      ref={columnRef}
      onAnimationComplete={() => {
        updateCirclePositions();
      }}
    >
      {elements.map((element) => {
        const typeCard = typeSelector(element);
        return (
          <WrappingCard
            key={element.id}
            handleClick={(id, checked) => {
              onSelect(id, checked);
            }}
            checked={element.id === selectedElement}
            disabled={disabled(element.id)}
            position={position}
            id={element.id}
            wrong={wrong === element.id}
            limLives={limLives}
            circleRef={(circleRef) => {
              circleRefs.current[element.id] = circleRef;
            }}
          >
            {typeCard === CARD_TYPES.IMAGE && (
              <ImageCard sourceImage={element.media.value} />
            )}
            {typeCard === CARD_TYPES.TEXT && (
              <TextCard sourceText={element.value} />
            )}
            {typeCard === CARD_TYPES.TEXT_IMAGE && (
              <TextImageCard
                sourceImage={element.media.value}
                sourceText={element.value}
                className={clsx(position === TYPE_OF_COLUMN.LEFT && styles.elementLeft) }
              />
            )}
            {typeCard === CARD_TYPES.AUDIO && (
              <AudioCard
                sourceAudio={element.media.value}
                isSelected={element.id === selectedElement}
                isCorrect={disabled(element.id)}
              />
            )}
            {typeCard === CARD_TYPES.TEXT_AUDIO && (
              <TextAudioCard
                sourceAudio={element.media.value}
                sourceText={element.value}
                isSelected={element.id === selectedElement}
                isCorrect={disabled(element.id)}
                className={clsx(position === TYPE_OF_COLUMN.LEFT && styles.elementLeft)}
              />
            )}
          </WrappingCard>
        );
      })}
    </motion.div>
  );
});

